/**
 * ReportDetailView — selected report's full thread.
 *
 * Renders: status pill + description + screenshot + comment thread +
 * compose box + (when status=awaiting_validation) "Mark as resolved"
 * button. Polls every 30 s while open so the user sees an operator's
 * reply without manually refreshing.
 */

import { h } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'

import type { ApiClient } from '../api/client'
import type {
  CapturedContext,
  ConsoleEntry,
  ErrorEntry,
  NetworkEntry,
  WidgetCommentRow,
  WidgetReportDetail,
  WidgetStatusChangeRow,
} from '../types'
import { CommentBubble } from './CommentBubble'
import type { StringKey } from './i18n'
import { safeExternalHref, truncateUrl } from './screenshot-utils'

interface ReportDetailViewProps {
  api: ApiClient
  externalId: string
  reportId: string
  strings: Record<StringKey, string>
  onBack: () => void
  /** When false, the self-service "Confirm resolution" button is hidden
   *  even if the report is in awaiting_validation. The Board passes false
   *  for project-scoped rows that don't belong to the current user. */
  canModerate?: boolean
  /** Layout flavor. "modal" (default) keeps the original My-reports look —
   *  scrollable card centered in the modal. "board" tightens the padding
   *  + drops the top "← back" because the Board renders the back action
   *  in its own header. */
  variant?: 'modal' | 'board'
}

const POLL_MS = 30_000

export function ReportDetailView({
  api,
  externalId,
  reportId,
  strings,
  onBack,
  canModerate = true,
  variant = 'modal',
}: ReportDetailViewProps) {
  const [detail, setDetail] = useState<WidgetReportDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [composeBody, setComposeBody] = useState('')
  const [sending, setSending] = useState(false)
  const [closing, setClosing] = useState(false)
  const mountedRef = useRef(true)

  const fetchDetail = async () => {
    try {
      const next = await api.getReport(reportId, externalId)
      if (!mountedRef.current) return
      setDetail(next)
      setError(null)
    } catch (err) {
      // The verbatim `err.message` (e.g. `getReport failed: 404 …`) leaked
      // straight into the panel pre-v0.15.3. Friendly UI via the
      // `!detail` empty-state branch below; raw error to console.
      if (typeof console !== 'undefined') console.warn('[mhosaic] getReport:', err)
      if (!mountedRef.current) return
      // `load_failed` is a sentinel — the render branch swaps to the
      // friendly empty-state UI when `error` is truthy and `detail` is
      // null. The string itself never reaches the DOM.
      setError('load_failed')
    }
  }

  useEffect(() => {
    mountedRef.current = true
    void fetchDetail()
    const timer = setInterval(() => {
      void fetchDetail()
    }, POLL_MS)
    return () => {
      mountedRef.current = false
      clearInterval(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, externalId])

  const handleSend = async () => {
    if (!composeBody.trim() || sending) return
    setSending(true)
    try {
      const nonce = `${reportId}:${Date.now()}`
      const created = await api.addComment(reportId, externalId, composeBody.trim(), nonce)
      if (!mountedRef.current) return
      setComposeBody('')
      // Optimistically append + then refetch so server-rendered timestamps
      // and any race with operator replies stay coherent.
      setDetail((prev) =>
        prev ? { ...prev, comments: appendComment(prev.comments, created) } : prev,
      )
      void fetchDetail()
    } catch (err) {
      // Don't surface raw API messages (e.g. `addComment failed: 403 …`).
      // Friendly fallback in the UI, raw error in the console.
      if (typeof console !== 'undefined') console.warn('[mhosaic] addComment:', err)
      if (!mountedRef.current) return
      setError(strings['detail.send_failed'])
    } finally {
      if (mountedRef.current) setSending(false)
    }
  }

  const handleClose = async () => {
    if (closing) return
    setClosing(true)
    try {
      const next = await api.closeAsResolved(reportId, externalId)
      if (!mountedRef.current) return
      setDetail(next)
    } catch (err) {
      if (typeof console !== 'undefined')
        console.warn('[mhosaic] closeAsResolved:', err)
      if (!mountedRef.current) return
      setError(strings['detail.close_failed'])
    } finally {
      if (mountedRef.current) setClosing(false)
    }
  }

  if (!detail && !error) {
    return <div class="mine-loading">{strings['mine.loading']}</div>
  }

  if (!detail) {
    // The raw `error.message` from the api client used to render here as
    // e.g. `getReport failed: 404 {"detail":"Report not found."}` — a
    // verbatim leak of the backend error. The friendly variant + a Back
    // button is what end users (and external testers) expect to see.
    return (
      <div class="report-detail-empty-state" role="alert">
        <p class="report-detail-empty-state-title">
          {strings['detail.load_failed.title']}
        </p>
        <p class="report-detail-empty-state-body">
          {strings['detail.load_failed.body']}
        </p>
        <button type="button" class="btn" onClick={onBack}>
          ← {strings['detail.load_failed.cta']}
        </button>
      </div>
    )
  }

  // Permission gating. `canModerate` is passed by the parent (Board
  // passes `is_mine`; My-reports defaults to true). v0.15.3 also reads
  // `detail.is_mine` from the server when present, so a direct fetch
  // (no parent context) still gates correctly.
  const isMine = detail.is_mine ?? canModerate
  // "Mark as resolved" is the only self-service status transition
  // the widget allows (awaiting_validation → closed).
  const showCloseCta = isMine && detail.status === 'awaiting_validation'
  // Reply compose box is submitter-only. Replies are private convo with
  // the operator team, even when the report itself is project-visible
  // via `share_reports_with_widget`. Hidden (not just disabled) when
  // not the submitter, since the action is genuinely unavailable.
  const showComposeBox = isMine

  return (
    <div class={`report-detail variant-${variant}`}>
      {variant === 'modal' && (
        <div class="report-detail-header">
          <button type="button" class="btn" onClick={onBack}>
            ← {strings['detail.back']}
          </button>
          <span class={`pill pill-status pill-status--${detail.status}`}>
            {strings[`status.${detail.status}` as StringKey] ?? detail.status}
          </span>
        </div>
      )}
      {variant === 'board' && (
        <div class="report-detail-header report-detail-header--board">
          <button
            type="button"
            class="btn btn--ghost report-detail-back"
            onClick={onBack}
            aria-label={strings['board.back']}
          >
            ← {strings['board.back']}
          </button>
          <span class={`pill pill-status pill-status--${detail.status}`}>
            {strings[`status.${detail.status}` as StringKey] ?? detail.status}
          </span>
        </div>
      )}

      <div class="report-detail-body">
        <ContextBlock detail={detail} strings={strings} />
        <p class="report-detail-description">{detail.description}</p>
        {detail.screenshot_url && (() => {
          const safeHref = safeExternalHref(detail.screenshot_url)
          // No clickable open-in-new-tab for non-http(s) screenshot_url
          // (defense-in-depth — server returns presigned https URLs, but
          // we don't trust client-rendered content with the operator's
          // origin). The inline preview is `&lt;img src&gt;`, which is safe
          // even if the URL is weird because Preact escapes attrs and
          // the browser refuses to render non-image content.
          return safeHref ? (
            <a
              class="report-detail-screenshot"
              href={safeHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={detail.screenshot_url} alt="" loading="lazy" />
            </a>
          ) : (
            <div class="report-detail-screenshot">
              <img src={detail.screenshot_url} alt="" loading="lazy" />
            </div>
          )
        })()}

        <h3 class="report-detail-section">{strings['detail.thread']}</h3>
        {detail.comments.length === 0 ? (
          <p class="report-detail-empty">{strings['detail.no_replies']}</p>
        ) : (
          <ul class="report-comments">
            {detail.comments.map((c) => (
              <li>
                <CommentBubble comment={c} strings={strings} />
              </li>
            ))}
          </ul>
        )}

        {detail.status_history && detail.status_history.length > 0 && (
          <StatusHistorySection rows={detail.status_history} strings={strings} />
        )}

        {detail.technical_context && (
          <TechnicalContextDrawer ctx={detail.technical_context} strings={strings} />
        )}

        {showComposeBox ? (
          <div class="report-compose">
            <textarea
              value={composeBody}
              placeholder={strings['detail.compose_placeholder']}
              onInput={(e) =>
                setComposeBody((e.target as HTMLTextAreaElement).value)
              }
              disabled={sending}
            />
            <div class="report-compose-actions">
              {showCloseCta && (
                <button
                  type="button"
                  class="btn"
                  onClick={handleClose}
                  disabled={closing}
                >
                  {closing
                    ? strings['detail.close_busy']
                    : strings['detail.close_cta']}
                </button>
              )}
              <button
                type="button"
                class="btn btn--primary"
                onClick={handleSend}
                disabled={!composeBody.trim() || sending}
              >
                {sending
                  ? strings['detail.compose_sending']
                  : strings['detail.compose_send']}
              </button>
            </div>
          </div>
        ) : (
          // Teammate is reading a project-wide row. Replies are private
          // to the submitter, so we hide the compose box entirely (action
          // is genuinely unavailable) and surface a short notice so the
          // viewer understands why.
          <p class="report-detail-teammate-notice" role="note">
            {strings['detail.teammate_notice']}
          </p>
        )}

        {error && <div class="error">{error}</div>}
      </div>
    </div>
  )
}

function appendComment(
  current: WidgetCommentRow[],
  next: WidgetCommentRow,
): WidgetCommentRow[] {
  if (current.some((c) => c.id === next.id)) return current
  return [...current, next]
}

interface ContextBlockProps {
  detail: WidgetReportDetail
  strings: Record<StringKey, string>
}

/**
 * The original-context block that sits above the description on the
 * detail page. Surfaces what the user originally reported (type +
 * severity at submit time, page they were on, when they sent it,
 * how the screenshot was captured) so they can recall the report
 * without having to re-derive it from the description.
 */
function ContextBlock({ detail, strings }: ContextBlockProps) {
  const captureKey: StringKey = `detail.context.capture.${detail.capture_method}` as StringKey
  const captureLabel = strings[captureKey] ?? detail.capture_method
  return (
    <div class="report-detail-context">
      <div class="report-detail-context-pills">
        <span class="pill pill-type">{strings[`type.${detail.feedback_type}` as StringKey]}</span>
        <span class={`pill pill-severity pill-severity--${detail.severity}`}>
          {strings[`severity.${detail.severity}` as StringKey]}
        </span>
        <span class="pill pill-capture">{captureLabel}</span>
      </div>
      <div class="report-detail-context-line">
        <span class="report-detail-context-label">{strings['detail.context.submitted_at']}</span>
        <span>{formatSubmittedAt(detail.created_at)}</span>
      </div>
      {detail.page_url && (() => {
        const safeHref = safeExternalHref(detail.page_url)
        return (
          <div class="report-detail-context-line" title={detail.page_url}>
            <span class="report-detail-context-label">{strings['detail.context.page']}</span>
            {safeHref ? (
              <a
                class="report-detail-context-url"
                href={safeHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                {truncateUrl(detail.page_url, 64)}
              </a>
            ) : (
              <span class="report-detail-context-url">{truncateUrl(detail.page_url, 64)}</span>
            )}
          </div>
        )
      })()}
    </div>
  )
}

function formatSubmittedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  } catch {
    return iso
  }
}

interface TechnicalContextDrawerProps {
  ctx: CapturedContext
  strings: Record<StringKey, string>
}

const TECH_CONSOLE_LIMIT = 20
const TECH_NETWORK_LIMIT = 15

/**
 * The "transparency drawer" — collapsible panel showing the user the
 * exact diagnostic data their browser submitted with the report (console
 * tail, network tail, JS errors, device summary). Closed by default so
 * the regular conversation flow stays uncluttered; one click reveals
 * everything. Borrowed from thePnr's customer-facing `TechnicalContextBlock`.
 */
function TechnicalContextDrawer({ ctx, strings }: TechnicalContextDrawerProps) {
  const errors = ctx.errors ?? []
  const consoleLogs = (ctx.consoleLogs ?? []).slice(-TECH_CONSOLE_LIMIT)
  const network = (ctx.networkRequests ?? []).slice(-TECH_NETWORK_LIMIT)
  const device = ctx.device
  const hasAny =
    !!device || errors.length > 0 || consoleLogs.length > 0 || network.length > 0
  if (!hasAny) return null
  const errorCount = errors.length
  const summary =
    errorCount > 0
      ? `${strings['detail.tech.title']} · ${errorCount} ${
          errorCount === 1 ? strings['detail.tech.errors_one'] : strings['detail.tech.errors_many']
        }`
      : strings['detail.tech.title']
  return (
    <details class="report-detail-tech">
      <summary>{summary}</summary>
      <div class="tech-body">
        {device && <DeviceSection device={device} strings={strings} />}
        {errors.length > 0 && <ErrorsSection errors={errors} strings={strings} />}
        {consoleLogs.length > 0 && <ConsoleSection logs={consoleLogs} strings={strings} />}
        {network.length > 0 && <NetworkSection rows={network} strings={strings} />}
      </div>
    </details>
  )
}

function DeviceSection({
  device,
  strings,
}: {
  device: CapturedContext['device']
  strings: Record<StringKey, string>
}) {
  const parts: Array<{ label: string; value: string }> = []
  if (device?.viewport) {
    const dpr = device.viewport.dpr ? ` @${device.viewport.dpr}x` : ''
    parts.push({
      label: strings['detail.tech.device.viewport'],
      value: `${device.viewport.w}×${device.viewport.h}${dpr}`,
    })
  }
  if (device?.platform) parts.push({ label: strings['detail.tech.device.platform'], value: device.platform })
  if (device?.language) parts.push({ label: strings['detail.tech.device.language'], value: device.language })
  if (device?.timezone) parts.push({ label: strings['detail.tech.device.timezone'], value: device.timezone })
  if (device?.connection) parts.push({ label: strings['detail.tech.device.connection'], value: device.connection })
  if (parts.length === 0) return null
  return (
    <div class="tech-section">
      <h4>{strings['detail.tech.device']}</h4>
      <dl class="tech-device">
        {parts.map((p) => (
          <>
            <dt>{p.label}</dt>
            <dd>{p.value}</dd>
          </>
        ))}
      </dl>
    </div>
  )
}

function ErrorsSection({
  errors,
  strings,
}: {
  errors: ErrorEntry[]
  strings: Record<StringKey, string>
}) {
  return (
    <div class="tech-section">
      <h4>{strings['detail.tech.errors']}</h4>
      <ul class="tech-errors">
        {errors.map((e) => (
          <li>
            <div class="tech-errors-msg">{e.message}</div>
            {e.stack && <pre class="tech-errors-stack">{truncateStack(e.stack)}</pre>}
          </li>
        ))}
      </ul>
    </div>
  )
}

function ConsoleSection({
  logs,
  strings,
}: {
  logs: ConsoleEntry[]
  strings: Record<StringKey, string>
}) {
  return (
    <div class="tech-section">
      <h4>{strings['detail.tech.console']}</h4>
      <ul class="tech-console">
        {logs.map((l) => (
          <li class={`tech-console-row tech-console-row--${l.level}`}>
            <span class="tech-console-level">{l.level}</span>
            <span class="tech-console-msg">{l.message}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function NetworkSection({
  rows,
  strings,
}: {
  rows: NetworkEntry[]
  strings: Record<StringKey, string>
}) {
  return (
    <div class="tech-section">
      <h4>{strings['detail.tech.network']}</h4>
      <ul class="tech-network">
        {rows.map((n) => {
          const failed = n.status >= 400 || n.status === 0
          return (
            <li class={`tech-network-row${failed ? ' tech-network-row--fail' : ''}`}>
              <span class="tech-network-status">{n.status || '—'}</span>
              <span class="tech-network-method">{n.method}</span>
              <span class="tech-network-url" title={n.url}>
                {truncateUrl(n.url, 56)}
              </span>
              <span class="tech-network-time">{Math.round(n.durationMs)}ms</span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function truncateStack(stack: string): string {
  // Clip to first 12 frames so the drawer doesn't blow out vertically on
  // a deep React stack — the user can always click through to a full view
  // if we add one later.
  const lines = stack.split('\n')
  if (lines.length <= 12) return stack
  return lines.slice(0, 12).join('\n') + '\n  …'
}

interface StatusHistorySectionProps {
  rows: WidgetStatusChangeRow[]
  strings: Record<StringKey, string>
}

/**
 * The full audit trail of who changed the report's status, when, and via
 * which surface (operator UI, MCP agent, or system). Read-only — purely
 * a transparency / "you're being heard by real humans" gesture borrowed
 * from thePnr's customer-facing detail view.
 */
function StatusHistorySection({ rows, strings }: StatusHistorySectionProps) {
  return (
    <div class="report-detail-history">
      <h3 class="report-detail-section">{strings['detail.history']}</h3>
      <ol class="status-history">
        {rows.map((r) => {
          const from = strings[`status.${r.from_status}` as StringKey] ?? r.from_status
          const to = strings[`status.${r.to_status}` as StringKey] ?? r.to_status
          const sourceKey: StringKey =
            r.changed_by_source === 'mcp'
              ? 'detail.author.mcp'
              : r.changed_by_source === 'system'
              ? 'detail.author.system'
              : 'detail.author.staff'
          return (
            <li class="status-history-row">
              <span class="status-history-time">{formatSubmittedAt(r.created_at)}</span>
              <span class="status-history-transition">
                <span class={`pill pill-status pill-status--${r.from_status}`}>{from}</span>
                <span class="status-history-arrow" aria-hidden="true">→</span>
                <span class={`pill pill-status pill-status--${r.to_status}`}>{to}</span>
              </span>
              <span class={`status-history-source status-history-source--${r.changed_by_source}`}>
                {strings[sourceKey]}
              </span>
            </li>
          )
        })}
      </ol>
    </div>
  )
}
